import { useEffect, useMemo, useState } from "react";
import "./ReportsSectionCompact.css";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import SBINModal from "../components/modals/SBINModal";
import ConfirmModalRose from "../components/modals/ConfirmModalRose";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import {
  getSuperAdminBugReportFilters,
  getSuperAdminBugReportSummary,
  listSuperAdminBugReports,
  updateSuperAdminBugReport,
} from "../api/bugReports";
import { BUG_REPORT_TOOLTIPS } from "../constants/reportTooltips";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import { formatDateOnlyYYYYMMDD } from "../utils/dateOnly";

const STATUS_BADGES = {
  Open: "border-amber-200 bg-amber-50 text-amber-700",
  "In Progress": "border-blue-200 bg-blue-50 text-blue-700",
  Resolved: "border-emerald-200 bg-emerald-50 text-emerald-700",
  Dismissed: "border-slate-200 bg-slate-100 text-slate-600",
};

const SEVERITY_BADGES = {
  Low: "border-slate-200 bg-slate-50 text-slate-600",
  Medium: "border-amber-200 bg-amber-50 text-amber-700",
  High: "border-orange-200 bg-orange-50 text-orange-700",
  Critical: "border-rose-200 bg-rose-50 text-rose-700",
};

const HELP_ANCHORS = {
  overview: "sa-bug-reports-overview",
  summary: "sa-bug-reports-summary",
  filters: "sa-bug-reports-filters",
  table: "sa-bug-reports-table",
  details: "sa-bug-reports-details",
};

const HELP_GUIDE = {
  title: "Help Guide: Bug Reports (Super Admin)",
  subtitle: "Track, triage, and resolve user-submitted bugs.",
  sections: [
    {
      id: "overview",
      title: "Overview",
      description:
        "Review user-submitted issues and screenshots. Export is not available on this page.",
      anchorId: HELP_ANCHORS.overview,
      bullets: [
        "Review user-submitted issues and screenshots.",
        "Filter by scope and date range.",
        "Open details to update status and add admin notes.",
      ],
    },
    {
      id: "summary-cards",
      title: "Summary Cards",
      description: "Quick snapshot of workload and urgency.",
      anchorId: HELP_ANCHORS.summary,
      bullets: [
        "Open / In Progress show current workload.",
        "Resolved (30D) shows recent completions.",
        "Critical (30D) highlights urgent issues.",
      ],
      tips: ["Use Critical + Open to find urgent items fast."],
    },
    {
      id: "scope-and-filters",
      title: "Scope and Filters",
      description:
        "Use salon-local accounting dates and scope filters to narrow results.",
      anchorId: HELP_ANCHORS.filters,
      bullets: [
        "Date range uses the salon's local accounting date.",
        "Business limits results to one business.",
        "Salon limits results to one salon.",
        "Search looks across title, description, and page URL.",
        "Status, Severity, and Category refine triage focus.",
      ],
      tips: [
        "Reset filters if results look empty.",
        "Search works best with short keywords.",
      ],
    },
    {
      id: "results-table",
      title: "Results Table",
      description: "Review filtered bug reports and open details.",
      anchorId: HELP_ANCHORS.table,
      bullets: [
        "Rows show submitted bug reports within your filter range.",
        "Click a row to open details.",
        "Screenshot icon indicates attachments.",
      ],
      tips: ["Sort/scan by Severity then Status to prioritize."],
    },
    {
      id: "bug-details",
      title: "Bug Details (Modal)",
      description: "Open a row to view full details and update status.",
      anchorId: HELP_ANCHORS.details,
      bullets: [
        "View full description, steps, expected vs actual.",
        "Open screenshot in a new tab.",
        "Update Status and add Admin Notes.",
        "Setting status to Resolved records a resolution time (if implemented).",
      ],
    },
  ],
  troubleshooting: [
    {
      title: "No results returned",
      body: "Widen date range; clear filters; confirm business/salon scope.",
    },
    {
      title: "Screenshot won't open",
      body: "Check attachment exists; refresh; verify static file access.",
    },
    {
      title: "Counts look off",
      body: "Confirm date range and salon timezone logic.",
    },
  ],
};

const formatDateISO = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildDefaultFilters = () => {
  const today = new Date();
  const end = formatDateISO(today);
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 29);
  return {
    date_from: formatDateISO(startDate),
    date_to: end,
    status: [],
    severity: [],
    category: [],
    business_id: "",
    salon_id: "",
    search: "",
    page: 1,
    page_size: 25,
  };
};

export default function SuperAdminBugReports() {
  const { is_super_admin, token } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();

  const [filters, setFilters] = useState(buildDefaultFilters());
  const [statusOptions, setStatusOptions] = useState([
    "Open",
    "In Progress",
    "Resolved",
    "Dismissed",
  ]);
  const [severityOptions, setSeverityOptions] = useState([
    "Low",
    "Medium",
    "High",
    "Critical",
  ]);
  const [categoryOptions, setCategoryOptions] = useState([
    "Booking",
    "Tickets",
    "Reports",
    "Time Clock",
    "Walk-In Queue",
    "Setup",
    "Other",
  ]);
  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [filtersError, setFiltersError] = useState("");
  const [listError, setListError] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailReport, setDetailReport] = useState(null);
  const [detailStatus, setDetailStatus] = useState("");
  const [detailNotes, setDetailNotes] = useState("");
  const [detailError, setDetailError] = useState("");
  const [savingDetail, setSavingDetail] = useState(false);
  const [confirmResolveOpen, setConfirmResolveOpen] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(null);

  const filteredSalons = useMemo(() => {
    if (!filters.business_id) return salons;
    return salons.filter(
      (salon) => String(salon.business_id || "") === String(filters.business_id)
    );
  }, [salons, filters.business_id]);

  const totalPages = useMemo(() => {
    if (!total || !filters.page_size) return 1;
    return Math.max(1, Math.ceil(total / filters.page_size));
  }, [total, filters.page_size]);

  useEffect(() => {
    if (!is_super_admin) return;
    let mounted = true;

    const loadFilters = async () => {
      setLoadingFilters(true);
      setFiltersError("");
      try {
        const data = await getSuperAdminBugReportFilters(token);
        if (!mounted) return;
        setStatusOptions(Array.isArray(data?.statuses) ? data.statuses : []);
        setSeverityOptions(Array.isArray(data?.severities) ? data.severities : []);
        setCategoryOptions(Array.isArray(data?.categories) ? data.categories : []);
        setBusinesses(Array.isArray(data?.businesses) ? data.businesses : []);
        setSalons(Array.isArray(data?.salons) ? data.salons : []);
      } catch (err) {
        if (!mounted) return;
        setFiltersError(err?.message || "Unable to load filters.");
      } finally {
        if (mounted) setLoadingFilters(false);
      }
    };

    const loadSummary = async () => {
      setLoadingSummary(true);
      try {
        const data = await getSuperAdminBugReportSummary(token);
        if (!mounted) return;
        setSummary(data || null);
      } catch (err) {
        if (!mounted) return;
        setSummary(null);
      } finally {
        if (mounted) setLoadingSummary(false);
      }
    };

    loadFilters();
    loadSummary();
    return () => {
      mounted = false;
    };
  }, [is_super_admin, token]);

  const fetchList = async (pageOverride = null, overrideFilters = null) => {
    setLoadingList(true);
    setListError("");
    const activeFilters = overrideFilters || filters;
    const nextPage = pageOverride ?? activeFilters.page;
    try {
      const payload = {
        ...activeFilters,
        page: nextPage,
        page_size: activeFilters.page_size,
        business_id: activeFilters.business_id
          ? Number(activeFilters.business_id)
          : undefined,
        salon_id: activeFilters.salon_id ? Number(activeFilters.salon_id) : undefined,
        status: activeFilters.status?.length ? activeFilters.status : undefined,
        severity: activeFilters.severity?.length
          ? activeFilters.severity
          : undefined,
        category: activeFilters.category?.length
          ? activeFilters.category
          : undefined,
        search: activeFilters.search?.trim() || undefined,
      };
      const data = await listSuperAdminBugReports(payload, token);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number(data?.total || 0));
      setFilters((prev) => ({ ...prev, page: Number(data?.page || nextPage) }));
    } catch (err) {
      setItems([]);
      setTotal(0);
      setListError(err?.message || "Unable to load bug reports.");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (!is_super_admin) return;
    fetchList(1);
  }, [is_super_admin]);

  const toggleFilterValue = (key, value) => {
    setFilters((prev) => {
      const current = prev[key] || [];
      if (current.includes(value)) {
        return { ...prev, [key]: current.filter((item) => item !== value) };
      }
      return { ...prev, [key]: [...current, value] };
    });
  };

  const handleBusinessChange = (event) => {
    const value = event.target.value;
    setFilters((prev) => ({
      ...prev,
      business_id: value,
      salon_id: prev.salon_id && value ? prev.salon_id : "",
    }));
  };

  const handleSalonChange = (event) => {
    const value = event.target.value;
    setFilters((prev) => ({ ...prev, salon_id: value }));
  };

  const handleResetFilters = () => {
    const reset = buildDefaultFilters();
    setFilters(reset);
    fetchList(1, reset);
  };

  const openDetail = (report) => {
    setDetailReport(report);
    setDetailStatus(report?.status || "");
    setDetailNotes(report?.admin_notes || "");
    setDetailError("");
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailReport(null);
    setDetailError("");
  };

  const runUpdate = async (payload) => {
    if (!detailReport) return;
    setSavingDetail(true);
    setDetailError("");
    try {
      const updated = await updateSuperAdminBugReport(
        detailReport.id,
        payload,
        token
      );
      setItems((prev) =>
        prev.map((item) => (item.id === updated.id ? updated : item))
      );
      setDetailReport(updated);
      setDetailOpen(false);
      setPendingUpdate(null);
      const summaryData = await getSuperAdminBugReportSummary(token);
      setSummary(summaryData || null);
    } catch (err) {
      setDetailError(err?.message || "Unable to update bug report.");
    } finally {
      setSavingDetail(false);
    }
  };

  const handleSaveDetail = () => {
    if (!detailReport) return;
    const payload = {};
    if (detailStatus && detailStatus !== detailReport.status) {
      payload.status = detailStatus;
    }
    if ((detailNotes || "") !== (detailReport.admin_notes || "")) {
      payload.admin_notes = detailNotes;
    }
    if (!Object.keys(payload).length) {
      closeDetail();
      return;
    }
    if (
      payload.status === "Resolved" &&
      detailReport.status !== "Resolved"
    ) {
      setPendingUpdate(payload);
      setConfirmResolveOpen(true);
      return;
    }
    runUpdate(payload);
  };

  const summaryCounts = summary?.counts_by_status || {};
  const recentStatus = summary?.recent?.status || {};
  const recentSeverity = summary?.recent?.severity || {};
  const renderFilterLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={BUG_REPORT_TOOLTIPS[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader title="Bug Reports" closeTo="/superadmin/reports" />
          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div
              id={HELP_ANCHORS.overview}
              className={`rounded-2xl ${getSectionClassName(HELP_ANCHORS.overview)}`}
            >
              <ReportHelpCard
                title={HELP_GUIDE.title}
                subtitle={HELP_GUIDE.subtitle}
                sections={HELP_GUIDE.sections}
                troubleshooting={HELP_GUIDE.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
            {filtersError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {filtersError}
              </div>
            )}
            {listError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {listError}
              </div>
            )}

            <div
              id={HELP_ANCHORS.summary}
              className={`grid grid-cols-1 md:grid-cols-4 gap-4 ${getSectionClassName(
                HELP_ANCHORS.summary
              )}`}
            >
              {[
                { label: "Open", value: summaryCounts.Open || 0 },
                { label: "In Progress", value: summaryCounts["In Progress"] || 0 },
                { label: "Resolved (30d)", value: recentStatus.Resolved || 0 },
                { label: "Critical (30d)", value: recentSeverity.Critical || 0 },
              ].map((card) => (
                <div
                  key={card.label}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    {card.label}
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {loadingSummary ? "..." : card.value}
                  </div>
                </div>
              ))}
            </div>

            <div
              id={HELP_ANCHORS.filters}
              className={`rounded-2xl border border-slate-200 bg-slate-50 p-4 md:p-6 space-y-4 ${getSectionClassName(
                HELP_ANCHORS.filters
              )}`}
            >
              <div className="grid gap-4 md:grid-cols-3">
                <div className="flex flex-col gap-1">
                  {renderFilterLabel("Date From", "date_from")}
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(event) =>
                      setFilters((prev) => ({
                        ...prev,
                        date_from: event.target.value,
                      }))
                    }
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  {renderFilterLabel("Date To", "date_to")}
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={(event) =>
                      setFilters((prev) => ({
                        ...prev,
                        date_to: event.target.value,
                      }))
                    }
                    className={FIELD_CLASS}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  {renderFilterLabel("Search", "search")}
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(event) =>
                      setFilters((prev) => ({
                        ...prev,
                        search: event.target.value,
                      }))
                    }
                    className={FIELD_CLASS}
                    placeholder="Title, description, or page URL"
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="flex flex-col gap-1">
                  {renderFilterLabel("Business", "business")}
                  <select
                    value={filters.business_id}
                    onChange={handleBusinessChange}
                    className={FIELD_CLASS}
                    disabled={loadingFilters}
                  >
                    <option value="">All Businesses</option>
                    {businesses.map((business) => (
                      <option key={business.id} value={business.id}>
                        {business.code} - {business.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  {renderFilterLabel("Salon", "salon")}
                  <select
                    value={filters.salon_id}
                    onChange={handleSalonChange}
                    className={FIELD_CLASS}
                    disabled={loadingFilters}
                  >
                    <option value="">All Salons</option>
                    {filteredSalons.map((salon) => (
                      <option key={salon.id} value={salon.id}>
                        {salon.name} (ID {salon.id})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  {renderFilterLabel("Status", "status")}
                  <div className="flex flex-wrap gap-2">
                    {(statusOptions || []).map((status) => (
                      <label
                        key={status}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={filters.status.includes(status)}
                          onChange={() => toggleFilterValue("status", status)}
                        />
                        {status}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-1">
                  {renderFilterLabel("Severity", "severity")}
                  <div className="flex flex-wrap gap-2">
                    {(severityOptions || []).map((severity) => (
                      <label
                        key={severity}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={filters.severity.includes(severity)}
                          onChange={() => toggleFilterValue("severity", severity)}
                        />
                        {severity}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  {renderFilterLabel("Category", "category")}
                  <div className="flex flex-wrap gap-2">
                    {(categoryOptions || []).map((category) => (
                      <label
                        key={category}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={filters.category.includes(category)}
                          onChange={() => toggleFilterValue("category", category)}
                        />
                        {category}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className={BTN_PRIMARY}
                  onClick={() => fetchList(1)}
                  disabled={loadingList}
                >
                  {loadingList ? "Applying..." : "Apply"}
                </button>
                <button
                  type="button"
                  className={BTN_OUTLINE}
                  onClick={handleResetFilters}
                >
                  Reset
                </button>
              </div>
            </div>

            <div
              id={HELP_ANCHORS.table}
              className={`rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden ${getSectionClassName(
                HELP_ANCHORS.table
              )} ${getSectionClassName(HELP_ANCHORS.details)}`}
            >
              <span id={HELP_ANCHORS.details} />
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600 text-xs font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <div className="flex items-center gap-1.5">
                          <span>Created</span>
                          <InfoTooltip
                            text={BUG_REPORT_TOOLTIPS.column_created}
                            label="Created"
                          />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <div className="flex items-center gap-1.5">
                          <span>Business / Salon</span>
                          <InfoTooltip
                            text={BUG_REPORT_TOOLTIPS.column_business_salon}
                            label="Business / Salon"
                          />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left">Title</th>
                      <th className="px-4 py-3 text-left">
                        <div className="flex items-center gap-1.5">
                          <span>Severity</span>
                          <InfoTooltip
                            text={BUG_REPORT_TOOLTIPS.column_severity}
                            label="Severity"
                          />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <div className="flex items-center gap-1.5">
                          <span>Status</span>
                          <InfoTooltip
                            text={BUG_REPORT_TOOLTIPS.column_status}
                            label="Status"
                          />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <div className="flex items-center gap-1.5">
                          <span>Reporter</span>
                          <InfoTooltip
                            text={BUG_REPORT_TOOLTIPS.column_reporter}
                            label="Reporter"
                          />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <div className="flex items-center gap-1.5">
                          <span>Page</span>
                          <InfoTooltip
                            text={BUG_REPORT_TOOLTIPS.column_page}
                            label="Page"
                          />
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left">
                        <div className="flex items-center gap-1.5">
                          <span>Screenshot</span>
                          <InfoTooltip
                            text={BUG_REPORT_TOOLTIPS.column_screenshot}
                            label="Screenshot"
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 && !loadingList ? (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-6 text-center text-slate-500"
                        >
                          No bug reports found for this range.
                        </td>
                      </tr>
                    ) : (
                      items.map((report) => {
                        const statusBadge =
                          STATUS_BADGES[report.status] ||
                          "border-slate-200 bg-slate-100 text-slate-600";
                        const severityBadge =
                          SEVERITY_BADGES[report.severity] ||
                          "border-slate-200 bg-slate-100 text-slate-600";
                        return (
                          <tr
                            key={report.id}
                            className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                            onClick={() => openDetail(report)}
                          >
                            <td className="px-4 py-3 text-slate-700">
                              <div className="text-sm font-semibold text-slate-900">
                                {formatDateOnlyYYYYMMDD(report.created_local_date)}
                              </div>
                              <div className="text-xs text-slate-500">
                                {report.salon_timezone || "--"}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-slate-700">
                              <div className="text-sm text-slate-900">
                                {report.business_name || report.business_id || "--"}
                              </div>
                              <div className="text-xs text-slate-500">
                                {report.salon_name || report.salon_id || "--"}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-slate-700 max-w-[240px]">
                              <div className="font-semibold text-slate-900">
                                {report.title}
                              </div>
                              <div className="text-xs text-slate-500">
                                {report.category}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-semibold ${severityBadge}`}
                              >
                                {report.severity}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-semibold ${statusBadge}`}
                              >
                                {report.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-700">
                              <div className="text-sm text-slate-900">
                                {report.created_by_name || "--"}
                              </div>
                              <div className="text-xs text-slate-500">
                                {report.created_by_email || "--"}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-slate-700">
                              <div className="max-w-[180px] truncate">
                                {report.page_url || "--"}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                type="button"
                                className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                                  report.screenshot_url
                                    ? "border-slate-300 text-slate-700 hover:bg-slate-50"
                                    : "border-slate-200 text-slate-400 cursor-not-allowed"
                                }`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (report.screenshot_url) {
                                    window.open(report.screenshot_url, "_blank");
                                  }
                                }}
                                disabled={!report.screenshot_url}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-slate-600">
              <div>
                Page {filters.page} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className={BTN_OUTLINE}
                  onClick={() => fetchList(Math.max(1, filters.page - 1))}
                  disabled={filters.page <= 1 || loadingList}
                >
                  Prev
                </button>
                <button
                  type="button"
                  className={BTN_OUTLINE}
                  onClick={() =>
                    fetchList(Math.min(totalPages, filters.page + 1))
                  }
                  disabled={filters.page >= totalPages || loadingList}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SBINModal
        isOpen={detailOpen}
        onClose={closeDetail}
        title={`Bug Report #${detailReport?.id || ""}`}
        size="xxl"
        footer={
          <div className="flex flex-wrap justify-end gap-3">
            <button type="button" className={BTN_OUTLINE} onClick={closeDetail}>
              Cancel
            </button>
            <button
              type="button"
              className={BTN_PRIMARY}
              onClick={handleSaveDetail}
              disabled={savingDetail}
            >
              {savingDetail ? "Saving..." : "Save"}
            </button>
          </div>
        }
      >
        {detailReport ? (
          <div className="space-y-6">
            {detailError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {detailError}
              </div>
            )}
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-lg md:text-xl font-semibold text-slate-900">
                  {detailReport.title}
                </div>
                <div className="text-sm text-slate-500">
                  {detailReport.category} -{" "}
                  {formatDateOnlyYYYYMMDD(detailReport.created_local_date)}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-semibold ${
                    STATUS_BADGES[detailReport.status] ||
                    "border-slate-200 bg-slate-100 text-slate-600"
                  }`}
                >
                  {detailReport.status}
                </span>
                <span
                  className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-semibold ${
                    SEVERITY_BADGES[detailReport.severity] ||
                    "border-slate-200 bg-slate-100 text-slate-600"
                  }`}
                >
                  {detailReport.severity}
                </span>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-1">
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  Reporter
                </div>
                <div className="text-sm font-semibold text-slate-900">
                  {detailReport.created_by_name || "--"}
                </div>
                <div className="text-xs text-slate-500">
                  {detailReport.created_by_email || "--"}
                </div>
                <div className="text-xs text-slate-500">
                  Role: {detailReport.role_snapshot || "--"}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-1">
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  Business / Salon
                </div>
                <div className="text-sm font-semibold text-slate-900">
                  {detailReport.business_name || detailReport.business_id || "--"}
                </div>
                <div className="text-xs text-slate-500">
                  {detailReport.salon_name || detailReport.salon_id || "--"}
                </div>
                <div className="text-xs text-slate-500">
                  Timezone: {detailReport.salon_timezone || "--"}
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-sm font-semibold text-slate-900 mb-2">
                  Description
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">
                  {detailReport.description || "--"}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-sm font-semibold text-slate-900 mb-2">
                  Steps to Reproduce
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">
                  {detailReport.steps_to_reproduce || "--"}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-sm font-semibold text-slate-900 mb-2">
                  Expected Result
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">
                  {detailReport.expected_result || "--"}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-sm font-semibold text-slate-900 mb-2">
                  Actual Result
                </div>
                <div className="text-sm text-slate-700 whitespace-pre-wrap">
                  {detailReport.actual_result || "--"}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2">
              <div className="text-sm text-slate-700">
                <span className="font-semibold">Page URL:</span>{" "}
                {detailReport.page_url || "--"}
              </div>
              <div className="text-sm text-slate-700">
                <span className="font-semibold">User Agent:</span>{" "}
                {detailReport.user_agent || "--"}
              </div>
            </div>

            {detailReport.screenshot_url && (
              <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
                <div className="text-sm font-semibold text-slate-900">
                  Screenshot
                </div>
                <img
                  src={detailReport.screenshot_url}
                  alt="Bug screenshot"
                  className="max-h-[360px] w-auto rounded-xl border border-slate-200"
                />
                <button
                  type="button"
                  className={BTN_OUTLINE}
                  onClick={() =>
                    window.open(detailReport.screenshot_url, "_blank")
                  }
                >
                  Open Full Image
                </button>
              </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <label className={LABEL_TEXT}>Status</label>
                  <select
                    value={detailStatus}
                    onChange={(event) => setDetailStatus(event.target.value)}
                    className={FIELD_CLASS}
                  >
                    {(statusOptions || []).map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className={LABEL_TEXT}>Resolved At</label>
                  <input
                    type="text"
                    value={detailReport.resolved_at || ""}
                    className={`${FIELD_CLASS} bg-slate-50 text-slate-500`}
                    disabled
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <label className={LABEL_TEXT}>Admin Notes</label>
                <textarea
                  value={detailNotes}
                  onChange={(event) => setDetailNotes(event.target.value)}
                  className={`${FIELD_CLASS} h-auto min-h-[140px] py-3`}
                  placeholder="Add internal notes or resolution details."
                />
              </div>
            </div>
          </div>
        ) : null}
      </SBINModal>

      <ConfirmModalRose
        open={confirmResolveOpen}
        title="Mark as Resolved?"
        message="Confirm resolving this bug report. Resolved time will be set automatically."
        confirmText="Resolve"
        cancelText="Cancel"
        onCancel={() => {
          setConfirmResolveOpen(false);
          setPendingUpdate(null);
        }}
        onConfirm={() => {
          if (pendingUpdate) {
            setConfirmResolveOpen(false);
            runUpdate(pendingUpdate);
          }
        }}
      />
    </div>
  );
}
